// Desk booking: a walk-in seated now with a free stylist, or a booking for later with a stylist or
// any available stylist, at one branch. Times come from that branch's live schedule, so nothing can be
// double booked; the service comes from the branch's service list (its exact id and price are saved).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import api, { addDays, formatDate, isoDate } from '../services/api';
import { useEngagementScope } from '../context/EngagementScope';
import BranchSelect from './BranchSelect';
import { Confirm, Modal, Spinner } from './ui';

const NOW_STATE = {
  free: { label: 'Free now', tone: 'ok' },
  busy: { label: 'Busy', tone: 'danger' },
  off: { label: 'Marked off', tone: 'muted' },
  none: { label: 'Not on the schedule now', tone: 'warn' },
};

const digits = (v) => String(v || '').replace(/\D/g, '');

function niceDay(iso) {
  const today = isoDate();
  if (iso === today) return 'Today';
  if (iso === addDays(today, 1)) return 'Tomorrow';
  return new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short' });
}

/**
 * preset: { mode: 'walk_in' | 'later', date, staffId, slotId, any, time }
 */
export default function BookingModal({ preset = {}, onClose, onBooked, onSeveral, partnerScopeReady = true }) {
  const scope = useEngagementScope();
  // HQ looking at more than one branch picks the branch here; otherwise it is the branch in view (or the desk's own).
  const soleBranch = scope.selectedIds.length === 1 ? scope.selectedIds[0] : '';
  const pickBranch = scope.scopeType !== 'franchise' && scope.branches.length > 1 && !soleBranch;
  const [branch, setBranch] = useState(preset.franchiseId ? String(preset.franchiseId) : soleBranch);
  const branchReady = !pickBranch || Boolean(branch);
  const [overlap, setOverlap] = useState(null);
  const [mode, setMode] = useState(preset.mode || 'later');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  // Gender of the person the booking is for: the salon desk needs it before any service, and it narrows the list.
  const [gender, setGender] = useState('');
  const autoGender = useRef(''); // the gender filled in from the last lookup, so a new number can replace it
  const [service, setService] = useState('');
  const [known, setKnown] = useState(null);
  const [services, setServices] = useState([]);
  const [nowData, setNowData] = useState(null);
  const [walkStaff, setWalkStaff] = useState(preset.mode === 'walk_in' ? preset.staffId || null : null);
  const [day, setDay] = useState(preset.date || isoDate());
  const [who, setWho] = useState(preset.any ? 'any' : preset.staffId ? String(preset.staffId) : 'any');
  const [board, setBoard] = useState(null);
  const [pick, setPick] = useState(
    preset.slotId ? { slot_id: preset.slotId, time: preset.time } : preset.any && preset.time ? { starts_at: `${preset.date}T${preset.time}:00`, time: preset.time } : null,
  );
  const [notifyTouched, setNotifyTouched] = useState(false);
  const [notify, setNotify] = useState((preset.mode || 'later') !== 'walk_in');
  const [saving, setSaving] = useState(false);
  const lookupTimer = useRef(null);
  const autoName = useRef(''); // the name filled in from the last lookup, so a new number can replace it

  useEffect(() => {
    if (!branchReady) return;
    setServices([]);
    api
      .listServices(pickBranch ? branch : undefined)
      .then((res) => setServices(res?.services || []))
      .catch(() => setServices([]));
  }, [branch, branchReady, pickBranch]);

  useEffect(() => {
    if (mode !== 'walk_in' || !partnerScopeReady || !branchReady) return;
    api
      .scheduleNow(pickBranch ? branch : undefined)
      .then(setNowData)
      .catch((e) => toast.error(e.message));
  }, [mode, partnerScopeReady, branch, branchReady, pickBranch]);

  useEffect(() => {
    if (mode !== 'later' || !partnerScopeReady || !branchReady) return;
    setBoard(null);
    api
      .scheduleDay(day, pickBranch ? branch : undefined)
      .then(setBoard)
      .catch((e) => toast.error(e.message));
  }, [mode, day, partnerScopeReady, branch, branchReady, pickBranch]);

  useEffect(() => {
    if (!notifyTouched) setNotify(mode !== 'walk_in');
  }, [mode, notifyTouched]);

  // Look the customer up by phone, to fill the name and show their last visit.
  useEffect(() => {
    clearTimeout(lookupTimer.current);
    const d = digits(phone);
    if (d.length < 10) {
      setKnown(null);
      return undefined;
    }
    lookupTimer.current = setTimeout(() => {
      api
        .lookupCustomer(d)
        .then((res) => {
          setKnown(res.found ? res : { found: false });
          const filled = res.found ? res.name || '' : '';
          // Fill the name, or swap it when the number changed; never overwrite what staff typed.
          setName((n) => (!n.trim() || n === autoName.current ? filled : n));
          autoName.current = filled;
          const savedGender = res.found ? res.gender || '' : '';
          setGender((g) => (!g || g === autoGender.current ? savedGender : g));
          autoGender.current = savedGender;
        })
        .catch(() => {});
    }, 350);
    return () => clearTimeout(lookupTimer.current);
  }, [phone]);

  const stylists = board?.stylists || [];
  const times = useMemo(() => {
    if (!board) return [];
    return board.rows
      .filter((r) => !r.past)
      .map((r) => {
        if (who === 'any') return r.any.left > 0 ? { time: r.time, label: r.label, starts_at: `${board.date}T${r.time}:00`, left: r.any.left } : null;
        const cell = r.cells[who];
        return cell && cell.state === 'free' ? { time: r.time, label: r.label, slot_id: cell.slot_id } : null;
      })
      .filter(Boolean);
  }, [board, who]);

  // Drop a picked time that is no longer offered (other day or stylist).
  useEffect(() => {
    if (!pick || !board) return;
    const match = times.find((t) => (pick.slot_id ? t.slot_id === pick.slot_id : t.starts_at === pick.starts_at));
    if (!match) setPick(null);
    else if (!pick.label) setPick(match); // a preset from the schedule gets its friendly label
  }, [times, board, pick]);

  const walkChoice = (nowData?.stylists || []).find((s) => s.id === walkStaff);
  const phoneOk = digits(phone).length >= 10;
  // Only the services for the chosen gender (services for everyone always show).
  const genderServices = gender
    ? services.filter((s) => {
        const forWho = (s.for || []).map((g) => String(g).toLowerCase());
        return !forWho.length || forWho.includes('everyone') || forWho.includes(gender);
      })
    : services;
  // The branch's list is the source of truth: pick one of its services (free text only when it has none).
  const chosenService = genderServices.find((s) => s.name.toLowerCase() === service.trim().toLowerCase()) || null;
  const serviceOk = services.length ? Boolean(chosenService) : Boolean(service.trim());
  const ready =
    branchReady &&
    phoneOk &&
    Boolean(gender) &&
    serviceOk &&
    (mode === 'walk_in' ? walkChoice && (walkChoice.state === 'free' || walkChoice.state === 'none') : Boolean(pick));

  const whoLabel =
    mode === 'walk_in'
      ? walkChoice?.name || ''
      : who === 'any'
        ? 'any available stylist'
        : stylists.find((s) => String(s.id) === who)?.name || '';
  const whenLabel = mode === 'walk_in' ? 'now' : pick ? `${niceDay(day)}, ${pick.label || pick.time}` : '';

  const submit = async (allowOverlap = false) => {
    if (!ready || saving) return;
    setSaving(true);
    try {
      const payload = {
        phone: digits(phone),
        customer_name: name.trim(),
        gender,
        service_name: chosenService ? chosenService.name : service.trim(),
        service_id: chosenService ? chosenService.service_id : null,
        franchise_id: pickBranch ? Number(branch) : soleBranch ? Number(soleBranch) : null,
        allow_overlap: allowOverlap,
        notify,
      };
      if (mode === 'walk_in') Object.assign(payload, { walk_in: true, staff_id: walkStaff });
      else if (pick.slot_id) payload.slot_id = pick.slot_id;
      else payload.starts_at = pick.starts_at;
      const res = await api.createAppointment(payload);
      toast.success(
        mode === 'walk_in'
          ? `${name.trim() || 'Walk-in'} is with ${whoLabel} now.`
          : `Booked: ${name.trim() || 'customer'} · ${service.trim()} · ${whenLabel} with ${whoLabel}.${notify ? ' WhatsApp confirmation sent.' : ''}`,
      );
      onBooked?.(res.appointment);
      onClose();
    } catch (e) {
      if (e.code === 'overlap') {
        setOverlap(e.message);
        return;
      }
      toast.error(e.message);
      const b = pickBranch ? branch : undefined;
      if (mode === 'later') api.scheduleDay(day, b).then(setBoard).catch(() => {});
      if (mode === 'walk_in') api.scheduleNow(b).then(setNowData).catch(() => {});
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="New appointment" onClose={onClose} size="wide">
      <div className="de-seg" role="tablist" aria-label="Booking type">
        <button type="button" role="tab" aria-selected={mode === 'walk_in'} className={mode === 'walk_in' ? 'active' : ''} onClick={() => setMode('walk_in')}>
          🚶 Walk-in now
        </button>
        <button type="button" role="tab" aria-selected={mode === 'later'} className={mode === 'later' ? 'active' : ''} onClick={() => setMode('later')}>
          📅 Book for later
        </button>
      </div>

      <div className="de-form-grid de-form-grid--2 de-mt-16">
        {pickBranch ? (
          <div className="de-field" style={{ gridColumn: '1 / -1' }}>
            <span className="de-label">
              Branch <span className="de-req">*</span>
            </span>
            <BranchSelect
              branches={scope.branches}
              value={branch}
              onChange={(next) => {
                setBranch(next);
                setPick(null);
                setWalkStaff(null);
                setWho('any');
                setService('');
              }}
              placeholder="Choose the branch…"
            />
          </div>
        ) : null}
        <label className="de-field">
          <span className="de-label">
            Mobile number <span className="de-req">*</span>
          </span>
          <div className="de-phone-input">
            <span>+91</span>
            <input
              className="de-input"
              inputMode="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="10-digit mobile"
              autoFocus
            />
          </div>
          {known?.found ? (
            <span className="de-small de-ok-text">
              Returning customer{known.name ? `: ${known.name}` : ''}
              {known.last_service ? ` · last: ${known.last_service} on ${formatDate(known.last_visit)}` : ''}
            </span>
          ) : known ? (
            <span className="de-small de-muted">New customer</span>
          ) : null}
        </label>
        <label className="de-field">
          <span className="de-label">Name on the booking</span>
          <input className="de-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Who is coming" />
          {known?.found && known.name_confirmed && name.trim() && name.trim().toLowerCase() !== (known.name || '').toLowerCase() ? (
            <span className="de-small de-muted">
              Booking for someone else on this number. {known.name} stays the contact; the booking is saved for {name.trim()}.
            </span>
          ) : known?.found && !known.name_confirmed && known.name ? (
            <span className="de-small de-muted">From their WhatsApp profile. Check it with them; it is saved as their name.</span>
          ) : null}
        </label>
        <div className="de-field" style={{ gridColumn: '1 / -1' }}>
          <span className="de-label">
            Gender <span className="de-req">*</span>
          </span>
          <div className="de-row" style={{ gap: 8 }} role="radiogroup" aria-label="Gender">
            {[
              ['male', 'Male'],
              ['female', 'Female'],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="radio"
                aria-checked={gender === key}
                className={`de-chip${gender === key ? ' active' : ''}`}
                onClick={() => {
                  setGender(key);
                  if (chosenService && !(chosenService.for || []).some((g) => ['everyone', key].includes(String(g).toLowerCase()))) setService('');
                }}
              >
                {label}
              </button>
            ))}
          </div>
          <span className="de-small de-muted">For the person coming in: the salon shows their services and needs it at check-in.</span>
        </div>
        <label className="de-field" style={{ gridColumn: '1 / -1' }}>
          <span className="de-label">
            Service <span className="de-req">*</span>
          </span>
          <input
            className="de-input"
            list="de-booking-services"
            value={service}
            onChange={(e) => setService(e.target.value)}
            placeholder={
              !branchReady ? 'Choose the branch first' : !gender ? 'Choose male or female first' : services.length ? "Search the branch's services" : 'e.g. Haircut'
            }
            disabled={!branchReady || !gender}
          />
          <datalist id="de-booking-services">
            {genderServices.map((s) => (
              <option key={s.service_id} value={s.name}>
                {[s.category, s.price_text].filter(Boolean).join(' · ')}
              </option>
            ))}
          </datalist>
          {chosenService ? (
            <span className="de-small de-ok-text">
              {chosenService.category ? `${chosenService.category} · ` : ''}
              {chosenService.price_text}
            </span>
          ) : service.trim() && services.length ? (
            <span className="de-small de-danger-text">
              Pick a service from this branch's list{gender ? ` for ${gender === 'male' ? 'male' : 'female'}` : ''}.
            </span>
          ) : null}
          {mode === 'later' && onSeveral ? (
            <button
              type="button"
              className="de-link-btn"
              style={{ alignSelf: 'flex-start', marginTop: 4 }}
              disabled={!branchReady || !gender}
              onClick={() =>
                onSeveral({
                  phone,
                  name,
                  gender,
                  branch: pickBranch ? branch : '',
                  day,
                  serviceIds: chosenService ? [chosenService.service_id] : [],
                })
              }
            >
              + Add another service (one visit, one confirmation)
            </button>
          ) : null}
        </label>
      </div>

      {mode === 'walk_in' ? (
        <div className="de-mt-16">
          <div className="de-section-label" style={{ marginBottom: 8 }}>
            Who will look after them now?
          </div>
          {!nowData ? (
            <Spinner />
          ) : nowData.stylists.length === 0 ? (
            <div className="de-small de-muted">No active stylists. Add one in Staff Availability → Stylists.</div>
          ) : (
            <div className="de-pick-grid">
              {nowData.stylists.map((s) => {
                const cfg = NOW_STATE[s.state] || NOW_STATE.none;
                const usable = s.state === 'free' || s.state === 'none';
                return (
                  <button
                    key={s.id}
                    type="button"
                    disabled={!usable}
                    className={`de-pick-card${walkStaff === s.id ? ' selected' : ''}`}
                    onClick={() => setWalkStaff(s.id)}
                  >
                    <div className="de-strong">{s.name}</div>
                    <span className={`de-pill de-pill--sm de-pill--${cfg.tone}`}>
                      {s.state === 'busy' ? `Busy until ${s.until}` : cfg.label}
                    </span>
                    {s.next_free && s.state !== 'free' ? <div className="de-small de-muted">Next free {s.next_free}</div> : null}
                  </button>
                );
              })}
            </div>
          )}
          {walkChoice?.state === 'none' ? (
            <div className="de-callout de-mt-8">
              {walkChoice.name} has no slot right now. The walk-in is saved anyway and keeps {walkChoice.name} busy for 30 minutes, or until you
              mark the service taken.
            </div>
          ) : null}
        </div>
      ) : (
        <div className="de-mt-16">
          <div className="de-row de-row--wrap" style={{ gap: 8, alignItems: 'center', marginBottom: 10 }}>
            <span className="de-section-label" style={{ margin: 0 }}>
              Day
            </span>
            {[isoDate(), addDays(isoDate(), 1), addDays(isoDate(), 2)].map((d) => (
              <button key={d} type="button" className={`de-chip${day === d ? ' active' : ''}`} onClick={() => setDay(d)}>
                {niceDay(d)}
              </button>
            ))}
            <input type="date" className="de-input de-input--bordered" style={{ width: 160, padding: '7px 10px' }} value={day} min={isoDate()} onChange={(e) => e.target.value && setDay(e.target.value)} />
          </div>
          <div className="de-row de-row--wrap" style={{ gap: 8, alignItems: 'center', marginBottom: 12 }}>
            <span className="de-section-label" style={{ margin: 0 }}>
              Stylist
            </span>
            <button type="button" className={`de-chip${who === 'any' ? ' active' : ''}`} onClick={() => setWho('any')}>
              Any available
            </button>
            {stylists.map((s) => (
              <button key={s.id} type="button" className={`de-chip${who === String(s.id) ? ' active' : ''}`} onClick={() => setWho(String(s.id))}>
                {s.name}
                {!s.working ? ' (not working)' : ''}
              </button>
            ))}
          </div>
          {!board ? (
            <Spinner />
          ) : times.length === 0 ? (
            <div className="de-callout">
              No free times {who === 'any' ? '' : `for ${whoLabel} `}on {niceDay(day)}. Try another day{who === 'any' ? '' : ' or Any available'}.
            </div>
          ) : (
            <div className="de-time-grid">
              {times.map((t) => {
                const selected = pick && (t.slot_id ? pick.slot_id === t.slot_id : pick.starts_at === t.starts_at);
                return (
                  <button key={t.time} type="button" className={`de-time-chip${selected ? ' selected' : ''}`} onClick={() => setPick(t)}>
                    {t.label}
                    {who === 'any' ? <span>{t.left} free</span> : null}
                  </button>
                );
              })}
            </div>
          )}
          {who === 'any' ? (
            <div className="de-small de-muted de-mt-8">
              An any-stylist booking holds one place at that time. Give it a stylist later from the Day schedule or Appointments.
            </div>
          ) : null}
        </div>
      )}

      <label className="de-check de-mt-16">
        <input
          type="checkbox"
          checked={notify}
          onChange={(e) => {
            setNotifyTouched(true);
            setNotify(e.target.checked);
          }}
        />
        <span>Send the WhatsApp confirmation and reminders to the customer</span>
      </label>

      <div className="de-booking-foot">
        <div className="de-small" style={{ flex: 1, minWidth: 200 }}>
          {ready ? (
            <>
              <strong>{name.trim() || 'Customer'}</strong> · {service.trim()} · {whenLabel} with {whoLabel}
            </>
          ) : (
            <span className="de-muted">
              {!branchReady
                ? 'Choose the branch.'
                : !phoneOk
                  ? 'Enter the mobile number.'
                  : !serviceOk
                    ? 'Pick the service.'
                    : mode === 'walk_in'
                      ? 'Pick a free stylist.'
                      : 'Pick a time.'}
            </span>
          )}
        </div>
        <button type="button" className="de-btn de-btn--outline" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="de-btn de-btn--primary" disabled={!ready || saving} onClick={() => submit(false)}>
          {saving ? <Spinner light /> : null}
          {mode === 'walk_in' ? 'Seat walk-in' : 'Book appointment'}
        </button>
      </div>
      {overlap ? (
        <Confirm
          title="Booking at another branch"
          message={`${overlap}. Book this one anyway?`}
          confirmLabel="Book anyway"
          busy={saving}
          onCancel={() => setOverlap(null)}
          onConfirm={() => {
            setOverlap(null);
            submit(true);
          }}
        />
      ) : null}
    </Modal>
  );
}
